import { useState } from 'react';
import { Download, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getStudentQuotasSummary, generateDownloadPdf } from '../../../api/drmQuotasService';
import type { BookFormat } from '../types';
import styles from '../ReaderPanels.module.css';
import { useAlert } from '../../../contexts/AlertContext';
import { useTranslation } from '../../../i18n/useTranslation';
import { controlClasses } from './controls';

interface DownloadPanelProps {
  bookId: string;
  totalPages: number;
  format: BookFormat;
}

export default function DownloadPanel({ bookId, totalPages, format }: DownloadPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useAlert();
  const studentId = user?.sub || '';

  const [startPage, setStartPage] = useState<string>('1');
  const [endPage, setEndPage] = useState<string>(totalPages.toString());
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch DRM quotas
  const {
    data: quotas,
    isLoading: isLoadingQuotas,
    error: quotasError,
  } = useQuery({
    queryKey: ['drm-quotas', studentId, bookId],
    queryFn: () => getStudentQuotasSummary(studentId, bookId),
    enabled: !!studentId && !!bookId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handleDownload = async () => {
    if (!quotas) return;

    const start = parseInt(startPage, 10);
    const end = parseInt(endPage, 10);

    // Validation
    if (isNaN(start) || isNaN(end)) {
      showToast(t('App.Reader.Download.ValidationInvalidPages'), { type: 'warning' });
      return;
    }

    if (start < 1 || end < 1) {
      showToast(t('App.Reader.Download.ValidationMinPage'), { type: 'warning' });
      return;
    }

    if (start > totalPages || end > totalPages) {
      showToast(t('App.Reader.Download.ValidationMaxPage', { totalPages }), { type: 'warning' });
      return;
    }

    if (start > end) {
      showToast(t('App.Reader.Download.ValidationStartEnd'), { type: 'warning' });
      return;
    }

    const pageCount = end - start + 1;

    if (pageCount > quotas.download.remaining) {
      showToast(
        t('App.Reader.Download.QuotaExceededMessage', { pageCount, remaining: quotas.download.remaining }),
        { type: 'warning', title: t('App.Reader.Download.QuotaExceededTitle') }
      );
      return;
    }

    setIsDownloading(true);
    try {
      // Generate watermarked PDF via backend API
      const result = await generateDownloadPdf({
        studentId,
        bookId,
        startPage: start,
        endPage: end,
      });

      if (!result.isApproved) {
        showToast(result.message, { type: 'error' });
        return;
      }

      if (!result.downloadUrl) {
        showToast(t('App.Reader.Download.GenerateFailed'), { type: 'error' });
        return;
      }

      // Trigger browser download
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = `pages-${start}-${end}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Optimistically update the cache with the authoritative remainingQuota
      // from the API response so the UI reflects the new value immediately.
      queryClient.setQueryData(
        ['drm-quotas', studentId, bookId],
        (old: typeof quotas | undefined) =>
          old
            ? { ...old, download: { ...old.download, remaining: result.remainingQuota } }
            : old
      );
      // Still invalidate so other quotas (print/copy) stay in sync with backend.
      queryClient.invalidateQueries({ queryKey: ['drm-quotas', studentId, bookId] });

      showToast(t('App.Reader.Download.Success', { remainingQuota: result.remainingQuota }), {
        type: 'success',
      });
    } catch (error) {
      console.error('Download failed:', error);
      showToast(t('App.Reader.Download.Error'), { type: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  if (!user) {
    return (
      <div className={styles.panel}>
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-5 h-5 text-neutral-700" />
          <h2 className="text-lg font-semibold text-neutral-900">{t('App.Reader.Download.Title')}</h2>
        </div>
        <p className="text-sm text-neutral-500">{t('App.Reader.Download.LoginRequired')}</p>
      </div>
    );
  }

  if (isLoadingQuotas) {
    return (
      <div className={styles.panel}>
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-5 h-5 text-neutral-700" />
          <h2 className="text-lg font-semibold text-neutral-900">{t('App.Reader.Download.Title')}</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t('App.Reader.Download.LoadingQuota')}</span>
        </div>
      </div>
    );
  }

  if (quotasError || !quotas) {
    return (
      <div className={styles.panel}>
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-5 h-5 text-neutral-700" />
          <h2 className="text-lg font-semibold text-neutral-900">{t('App.Reader.Download.Title')}</h2>
        </div>
        <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error-700">
            {t('App.Reader.Download.QuotaLoadError')}
          </p>
        </div>
      </div>
    );
  }

  const downloadRemaining = quotas.download.remaining;
  const isDownloadExhausted = downloadRemaining <= 0;

  return (
    <div className={styles.panel} style={{ width: '100%', padding: '16px' }}>
      {/* Quota Display */}
      <div
        className="mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-md text-xs"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="font-semibold text-neutral-700">{t('App.Reader.Download.Remaining')}:</span>{' '}
        <span
          className={`font-bold ${
            downloadRemaining > 10
              ? 'text-success-600'
              : downloadRemaining > 0
                ? 'text-warning-600'
                : 'text-error-600'
          }`}
        >
          {t('App.Reader.Download.RemainingPages', { count: downloadRemaining })}
        </span>
      </div>

      {isDownloadExhausted ? (
        <div className="flex items-start gap-2 p-2 bg-warning-50 border border-warning-200 rounded-md">
          <AlertCircle className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-warning-700">
            <p className="font-semibold mb-0.5">{t('App.Reader.Download.QuotaExhaustedTitle')}</p>
            <p>{t('App.Reader.Download.QuotaExhaustedMessage')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Page Range Selection */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label htmlFor="downloadStartPage" className={controlClasses.fieldLabel}>
                {t('App.Reader.Download.StartPage')}
              </label>
              <input
                id="downloadStartPage"
                type="number"
                min={1}
                max={totalPages}
                value={startPage}
                onChange={(e) => setStartPage(e.target.value)}
                className={controlClasses.input}
                placeholder="1"
              />
            </div>
            <div>
              <label htmlFor="downloadEndPage" className={controlClasses.fieldLabel}>
                {t('App.Reader.Download.EndPage')}
              </label>
              <input
                id="downloadEndPage"
                type="number"
                min={1}
                max={totalPages}
                value={endPage}
                onChange={(e) => setEndPage(e.target.value)}
                className={controlClasses.input}
                placeholder={totalPages.toString()}
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-neutral-500 mb-3">
            <span>{t('App.Reader.Download.TotalPages', { totalPages })}</span>
            <span>
              {t('App.Reader.Download.ToDownload')}{' '}
              <span className="font-semibold text-neutral-700">
                {(() => {
                  const start = parseInt(startPage, 10);
                  const end = parseInt(endPage, 10);
                  if (isNaN(start) || isNaN(end) || start > end) return '-';
                  return end - start + 1;
                })()}
              </span>
            </span>
          </div>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className={`${controlClasses.btnPrimary} w-full flex items-center justify-center gap-1.5`}
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('App.Reader.Download.Preparing')}</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>{t('App.Reader.Download.DownloadButton')}</span>
              </>
            )}
          </button>

          {/* Info */}
          <p className="mt-2 text-[11px] text-neutral-500 leading-snug">
            {t('App.Reader.Download.Note')}
          </p>
        </>
      )}
    </div>
  );
}
